export type CustomWindow = Window & {
  env?: {
    REACT_APP_AUTH0_DOMAIN?: string;
    REACT_APP_AUTH0_AUDIENCE?: string;
    REACT_APP_AUTH0_CLIENT_ID?: string;
    REACT_APP_AUTH0_ORGANIZATION?: string;
    REACT_APP_AUTH0_CONNECTION?: string;
  };
};

export const isAuthBypassed = (): boolean => import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === "true";
export const getDevUserEmail = (): string =>
  import.meta.env.DEV ? import.meta.env.VITE_DEV_USER_EMAIL || "dev@localhost" : "";

export const getLogoutReturnTo = (): string => {
  return `${window.location.origin}/logout`;
};

type StoredOidcLogoutContext = {
  idTokenHint: string;
};

type LogoutOptions = {
  post_logout_redirect_uri: string;
  extraQueryParams: {
    returnTo: string;
  };
  id_token_hint?: string;
};

const getStoredOidcLogoutContext = (): StoredOidcLogoutContext | undefined => {
  const storages = [localStorage, sessionStorage];

  for (const storage of storages) {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith("oidc.user:")) {
        continue;
      }

      const rawValue = storage.getItem(key);
      if (!rawValue) {
        continue;
      }

      try {
        const parsed = JSON.parse(rawValue) as { id_token?: string };
        const trimmedIdToken = parsed.id_token?.trim();
        if (!trimmedIdToken) {
          continue;
        }

        return {
          idTokenHint: trimmedIdToken,
        };
      } catch {
        continue;
      }
    }
  }

  return undefined;
};

export const getLogoutOptions = (): LogoutOptions => {
  const logoutContext = getStoredOidcLogoutContext();
  const returnTo = getLogoutReturnTo();

  return {
    post_logout_redirect_uri: returnTo,
    extraQueryParams: {
      returnTo,
    },
    ...(logoutContext ? { id_token_hint: logoutContext.idTokenHint } : {}),
  };
};

const clearStorageByPrefix = (storage: Storage, prefixes: string[]) => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key) {
      continue;
    }
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
};

export const clearClientAuthState = () => {
  clearStorageByPrefix(localStorage, ["oidc.user:", "persist:"]);
  clearStorageByPrefix(sessionStorage, ["oidc.user:", "persist:"]);
};

export const redirectTo = (url: string) => {
  window.location.assign(url);
};

type LogoutFn = (options?: LogoutOptions) => void;

export const logoutWithCleanup = (logout: LogoutFn) => {
  if (isAuthBypassed()) {
    logout();
    return;
  }
  const options = getLogoutOptions();
  logout(options);
};
